import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Send, ArrowLeft, MessageSquarePlus, X } from 'lucide-react';
import { getThreads, getThread, sendToStudent, Thread, ChatMessage } from '../api';
import { listMembers, MemberRow } from '../../members/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { useToast } from '../../../context/ToastContext';
import { readCache, writeCache } from '../../../services/localCache';

function fmtTime(s: string) {
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function Avatar({ id, hasPhoto, name, size = 9 }: { id: number; hasPhoto: number; name: string; size?: 8 | 9 }) {
  const cls = size === 8 ? 'h-8 w-8' : 'h-9 w-9'; // estáticas: el JIT de Tailwind no compila h-${size}
  return <PersonAvatar contactId={id} hasPhoto={hasPhoto} name={name} size={cls} />;
}

export default function ConversationsPanel() {
  const toast = useToast();
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [threads, setThreads] = useState<Thread[] | null>(() => readCache<Thread[]>('msg-threads') ?? null);
  const [sel, setSel] = useState<number | null>(null);
  const [thread, setThread] = useState<{ name: string; has_photo: number; msgs: ChatMessage[] } | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [picker, setPicker] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<MemberRow[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const loadThreads = useCallback(() => getThreads().then((r) => { setThreads(r.data); writeCache('msg-threads', r.data); }).catch(() => setThreads((prev) => prev ?? [])), []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = useCallback((accountId: number) => {
    setSel(accountId); setLoadingThread(true); setPicker(false);
    getThread(accountId)
      .then((r) => setThread({ name: r.student.name, has_photo: r.student.has_photo, msgs: r.data }))
      .finally(() => setLoadingThread(false));
  }, []);

  // auto-scroll al último mensaje
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.msgs.length]);

  // buscar alumnos en el picker
  useEffect(() => {
    if (!picker) return;
    const t = setTimeout(() => {
      listMembers({ search: q, status: 'all', page_size: 20 }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, picker]);

  async function send() {
    const body = draft.trim();
    if (!body || sel == null) return;
    setSending(true);
    try {
      await sendToStudent(sel, body);
      setDraft('');
      const r = await getThread(sel);
      setThread({ name: r.student.name, has_photo: r.student.has_photo, msgs: r.data });
      loadThreads();
    } catch (e: any) { toast.error(e?.message || 'No se pudo enviar'); }
    finally { setSending(false); }
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
      {/* ===== Lista de conversaciones ===== */}
      <div className={`${sel != null ? 'hidden md:block' : ''} rounded-2xl border border-line bg-surface`}>
        <div className="flex items-center justify-between gap-2 border-b border-line p-3">
          <span className="text-sm font-semibold text-content">Conversaciones</span>
          <button onClick={() => { setPicker(true); setSel(null); setThread(null); setQ(''); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent-solid px-2.5 py-1.5 text-xs font-semibold text-on-accent transition hover:bg-accent-solid/90">
            <MessageSquarePlus size={14} /> Nueva
          </button>
        </div>
        {!threads ? (
          <div className="space-y-2 p-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : threads.length === 0 ? (
          <div className="p-2"><EmptyState icon={MessageSquarePlus} title="Sin conversaciones" subtitle="Toca “Nueva” para escribirle a un alumno." /></div>
        ) : (
          <ul className="max-h-[60vh] divide-y divide-line/60 overflow-y-auto md:max-h-[70vh]">
            {threads.map((t) => {
              const unread = Number(t.unread) > 0;
              return (
                <li key={t.account_id}>
                  <button onClick={() => openThread(t.account_id)}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-surface-2 ${sel === t.account_id ? 'bg-surface-2' : ''}`}>
                    <Avatar id={t.account_id} hasPhoto={t.has_photo} name={t.name} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`truncate text-sm ${unread ? 'font-bold text-content' : 'font-medium text-content'}`}>{t.name}</span>
                        <span className="shrink-0 text-[10px] text-content-3">{fmtTime(t.last_at).replace(/,.*/, '')}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`truncate text-xs ${unread ? 'text-content-2' : 'text-content-3'}`}>
                          {t.last_role === 'staff' ? 'Tú: ' : ''}{t.last_body}
                        </span>
                        {unread && <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-accent" />}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ===== Panel derecho: chat o picker ===== */}
      <div className={`${sel == null && !picker ? 'hidden md:block' : ''} flex min-h-[50vh] flex-col rounded-2xl border border-line bg-surface`}>
        {picker ? (
          <>
            <div className="flex items-center gap-2 border-b border-line p-3">
              <Search size={16} className="text-content-3" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca un alumno por nombre…"
                className="flex-1 bg-transparent text-sm text-content outline-none placeholder:text-content-3" />
              <button onClick={() => setPicker(false)} className="text-content-3 hover:text-content"><X size={16} /></button>
            </div>
            <ul className="flex-1 divide-y divide-line/60 overflow-y-auto">
              {results.map((m) => (
                <li key={m.account_id}>
                  <button onClick={() => openThread(m.account_id)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-surface-2">
                    <Avatar id={m.account_id} hasPhoto={m.has_photo} name={m.firstname} size={8} />
                    <span className="truncate text-sm font-medium text-content">{m.firstname} {m.lastname}</span>
                  </button>
                </li>
              ))}
              {results.length === 0 && <li className="px-3 py-6 text-center text-sm text-content-3">Escribe para buscar un alumno.</li>}
            </ul>
          </>
        ) : sel == null ? (
          <div className="grid flex-1 place-items-center">
            <EmptyState icon={Send} title="Elige una conversación" subtitle="Selecciona un alumno de la izquierda o inicia una nueva." />
          </div>
        ) : (
          <>
            {/* Encabezado del hilo */}
            <div className="flex items-center gap-3 border-b border-line p-3">
              <button onClick={() => { setSel(null); setThread(null); }} className="md:hidden text-content-3 hover:text-content"><ArrowLeft size={18} /></button>
              {thread && <Avatar id={sel} hasPhoto={thread.has_photo} name={thread.name} size={8} />}
              <span className="font-semibold text-content">{thread?.name || '…'}</span>
            </div>

            {/* Mensajes */}
            <div className="flex-1 space-y-2 overflow-y-auto p-4" style={{ maxHeight: '55vh' }}>
              {loadingThread ? (
                <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-2/3" />)}</div>
              ) : thread && thread.msgs.length === 0 ? (
                <p className="py-8 text-center text-sm text-content-3">Aún no hay mensajes. Escribe el primero abajo. 👇</p>
              ) : (
                thread?.msgs.map((m) => {
                  const mine = m.sender_role === 'staff';
                  return (
                    <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-accent-solid text-on-accent' : 'bg-surface-2 text-content'}`}>
                        <p className="whitespace-pre-wrap break-words">{m.body}</p>
                        <div className={`mt-0.5 text-[10px] ${mine ? 'text-on-accent/70' : 'text-content-3'}`}>{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={endRef} />
            </div>

            {/* Composer */}
            <div className="flex items-end gap-2 border-t border-line p-3">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Escribe un mensaje…" rows={1}
                className="max-h-32 min-h-[40px] flex-1 resize-none rounded-xl border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue" />
              <button onClick={send} disabled={sending || !draft.trim()}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent-solid text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-40">
                <Send size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
